import {defineMessages, FormattedMessage} from 'react-intl';
import {useEffect, useState} from 'react';

import {Button, view} from '@sail/ui';

import {Conversation} from '@stripe-internal/conversations-client';
import {TopBar} from '@stripe-internal/conversations-ui';

import {ConversationSpeakerButton} from './ConversationSpeakerButton';
import {useApp} from '../../../core/hooks/appHooks';

const messages = defineMessages({
  chattingWithName: {
    id: 'conversations_assistant.liveChatTopBar.chattingWithName',
    description:
      'Text indicating name of support agent the user is chatting with',
    defaultMessage: 'Chatting with {name}',
  },
  connectingWithAgent: {
    id: 'conversations_assistant.liveChatTopBar.chattingWithGeneric',
    description: 'Text indicating who the user is chatting stripe support',
    defaultMessage: 'Connecting with agent',
  },
  endChatButton: {
    id: 'conversations_assistant.liveChatTopBar.endChatButton',
    description: 'Button to end conversation with an agent',
    defaultMessage: 'End chat',
  },
});

function agentParticipantNames(conversation: Conversation) {
  const participants = Object.values(conversation.participants);

  const names = participants
    .filter((p) => p.role === 'agent')
    .map((p) => p.displayName)
    .filter((name) => !!name);

  return names;
}

type Props = {
  conversation: Conversation;
};

function Title({conversation}: Props) {
  const [agentNames, setAgentNames] = useState<string[]>(
    agentParticipantNames(conversation),
  );

  useEffect(() => {
    const sub = conversation
      .participantsObservable()
      .subscribe(() => setAgentNames(agentParticipantNames(conversation)));

    return () => sub.unsubscribe();
  }, [conversation]);

  return (
    <view.div css={{font: 'label.small.emphasized'}}>
      {agentNames.length > 0 ? (
        <FormattedMessage
          {...messages.chattingWithName}
          values={{name: agentNames.join(', ')}}
        />
      ) : (
        <FormattedMessage {...messages.connectingWithAgent} />
      )}
    </view.div>
  );
}

function Controls({conversation}: Props) {
  const app = useApp();

  return (
    <view.div css={{stack: 'x', alignX: 'end'}}>
      <ConversationSpeakerButton conversation={conversation} />
      <Button
        size="small"
        onPress={() => app.close()}
        data-testid="conversations-widget-end-chat-button"
      >
        <FormattedMessage {...messages.endChatButton} />
      </Button>
    </view.div>
  );
}

export function LiveChatTopBar({conversation}: Props) {
  return (
    <TopBar data-testId="widget-topbar-live-chat">
      <view.div css={{stack: 'x'}}>
        <Title conversation={conversation} />
        <Controls conversation={conversation} />
      </view.div>
    </TopBar>
  );
}
